/**
 * Overlay SVG layer for arbitrary (non-hierarchical) node connections.
 * Rendered in a separate SVG on top of the main canvas so connection lines
 * never clip inside the primary SVG's transform group.
 */
import React from 'react';
import { NodeConnection, OstNode } from '../../types';
import { buildConnectionPath } from '../../utils/canvasUtils';

interface ConnectionOverlayProps {
    connections: NodeConnection[];
    connectPreviewPath: string | null;
    /** CSS transform string applied to the inner <g> to match canvas pan/zoom. */
    transform: string;
    nodeMap: Record<string, OstNode & { x: number; y: number }>;
}

export default function ConnectionOverlay({
    connections,
    connectPreviewPath,
    transform,
    nodeMap,
}: ConnectionOverlayProps) {
    if (connections.length === 0 && !connectPreviewPath) return null;

    return (
        <svg className='canvas-connect-overlay' aria-hidden='true'>
            <g transform={transform}>
                {connections.map((conn) => {
                    const fromNode = nodeMap[conn.fromNodeId];
                    const toNode = nodeMap[conn.toNodeId];
                    const fromPoint = fromNode
                        ? { x: fromNode.x + conn.fromOffset.x, y: fromNode.y + conn.fromOffset.y }
                        : { x: conn.fromOffset.x, y: conn.fromOffset.y };
                    const toPoint = toNode
                        ? { x: toNode.x + conn.toOffset.x, y: toNode.y + conn.toOffset.y }
                        : { x: conn.toOffset.x, y: conn.toOffset.y };

                    const d = buildConnectionPath(fromPoint, toPoint);
                    return (
                        <g key={`conn-${conn.id}`}>
                            <path
                                d={d}
                                fill='none'
                                stroke='#f55d0b'
                                strokeWidth={2.3}
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                opacity={0.9}
                                strokeDasharray='1 8'
                            />
                            <circle cx={fromPoint.x} cy={fromPoint.y} r={3.2} fill='#f55d0b' opacity={0.95} />
                            <circle cx={toPoint.x} cy={toPoint.y} r={3.2} fill='#f55d0b' opacity={0.95} />
                        </g>
                    );
                })}

                {connectPreviewPath && (
                    <path
                        d={connectPreviewPath}
                        fill='none'
                        stroke='#f55d0b'
                        strokeWidth={2}
                        strokeDasharray='1 8'
                        strokeLinecap='round'
                        pointerEvents='none'
                    />
                )}
            </g>
        </svg>
    );
}
